import React from 'react';
import { ChevronDown, ChevronRight, AlertCircle, XCircle } from 'lucide-react';
import { BidLineItem } from '../../models/bid';
import { VarianceBadge } from './VarianceBadge';
import {
  calculateChildrenSum,
  hasParentChildMismatch,
  calculateVariance,
  formatCurrency
} from '../../utils/lineItemHelpers';

interface LineItemCardProps {
  item: BidLineItem;
  index: number;
  level: number;
  parentIndex?: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

export const LineItemCard: React.FC<LineItemCardProps> = ({
  item,
  index,
  level,
  parentIndex,
  isExpanded,
  onToggleExpand
}) => {
  const hasChildren = item.children && item.children.length > 0;
  const childrenSum = calculateChildrenSum(item);
  const hasMismatch = hasParentChildMismatch(item);

  const itemNumber = level === 0 ? `${index + 1}` : `${parentIndex}.${index + 1}`;

  // Issues that aren't cost-related (those show as variance)
  // Also filter out arithmetic and mismatch errors since we show those as prominent banners
  const displayIssues = item.issues?.filter(issue =>
    !issue.type.includes('cost') &&
    !issue.type.includes('arithmetic') &&
    !issue.type.includes('mismatch') &&
    !issue.description?.toLowerCase().includes('laborCost') &&
    !issue.description?.toLowerCase().includes('children with contractor costs')
  ) || [];

  // For parents: sum up children's Fxer estimates (get both low and high)
  const fxerEstimateLow = hasChildren && item.children
    ? item.children.reduce((sum, child) => {
        if (child.costAnalysis?.estimate) {
          return sum + child.costAnalysis.estimate.totalLow;
        }
        return sum;
      }, 0)
    : item.costAnalysis?.estimate?.totalLow || null;

  const fxerEstimateHigh = hasChildren && item.children
    ? item.children.reduce((sum, child) => {
        if (child.costAnalysis?.estimate) {
          return sum + child.costAnalysis.estimate.totalHigh;
        }
        return sum;
      }, 0)
    : item.costAnalysis?.estimate?.totalHigh || null;

  const fxerEstimateMid = fxerEstimateLow !== null && fxerEstimateHigh !== null
    ? (fxerEstimateLow + fxerEstimateHigh) / 2
    : null;

  // Contractor's actual quote (for parents, use their stated total OR sum of children)
  const contractorTotal = item.totalCost !== null ? item.totalCost : childrenSum;

  // Only show variance badge when there's a flagged issue (not reasonable/in-range)
  const assessment = item.costAnalysis?.assessment?.toLowerCase();
  const hasCostIssue = assessment === 'overpriced' || assessment === 'suspiciously_low' || assessment === 'concern';
  const showVariance = hasCostIssue && contractorTotal !== null && fxerEstimateLow !== null && fxerEstimateHigh !== null && level === 0;

  const variance = showVariance
    ? calculateVariance(contractorTotal, fxerEstimateLow!, fxerEstimateHigh!)
    : null;

  // Check if child item is missing measurements (based on quantitySource from cost analysis)
  const hasMissingMeasurements = level === 1 &&
    (item.costAnalysis?.quantitySource === 'unable_to_determine' ||
     item.costAnalysis?.quantitySource === 'average_estimated');

  // Items Fxer couldn't actually price (no measurements/plans/counts). These are excluded
  // from the headline Fxer estimate total, so the line must say so rather than showing a
  // confident "Est: $X" that contradicts the total.
  const cannotVerify = item.costAnalysis?.unableToEstimate === true ||
    item.costAnalysis?.assessment?.toLowerCase() === 'unable_to_estimate';

  // If this is a child, render as compact row
  if (level === 1) {
    // Calculate total estimate range for child
    const childEstimateTotal = item.costAnalysis?.estimate
      ? {
          low: (item.costAnalysis.estimate.laborCostLow || 0) + item.costAnalysis.estimate.materialCostLow,
          high: (item.costAnalysis.estimate.laborCostHigh || 0) + item.costAnalysis.estimate.materialCostHigh
        }
      : null;

    return (
      <div className="py-2 px-4 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
        {/* Main row with description and amounts */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-600 flex-shrink-0 w-8">{itemNumber}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-900 leading-tight">{item.description}</div>
              {/* Structural badge for child items */}
              {item.costAnalysis?.isStructural && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded border border-yellow-300">
                  ⚠️ Structural
                </span>
              )}
              {/* No Measurements badge */}
              {hasMissingMeasurements && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded border border-yellow-300">
                  <AlertCircle className="h-3 w-3" />
                  No Measurements
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {displayIssues.length > 0 && (
              <div className="flex items-center gap-1">
                <XCircle className="h-3.5 w-3.5 text-red-500" />
                <span className="text-xs text-red-600 font-medium">{displayIssues.length}</span>
              </div>
            )}
            <div className="text-right w-24">
              {item.totalCost !== null ? (
                <div className="text-sm font-medium text-gray-900 leading-tight">{formatCurrency(item.totalCost)}</div>
              ) : (
                <span className="text-xs text-gray-400 italic">No quote</span>
              )}
            </div>
          </div>
        </div>

        {/* Fxer estimate breakdown - multi-line below description */}
        {item.costAnalysis?.estimate && (
          <div className="mt-1 ml-11 text-xs text-gray-600 space-y-0.5">
            <div className="flex items-center gap-3">
              <span className="font-medium">Fxer Est:</span>
              <span>Labor {formatCurrency(item.costAnalysis.estimate.laborCostLow || 0)}-{formatCurrency(item.costAnalysis.estimate.laborCostHigh || 0)}</span>
              <span>•</span>
              <span>Material {formatCurrency(item.costAnalysis.estimate.materialCostLow)}-{formatCurrency(item.costAnalysis.estimate.materialCostHigh)}</span>
            </div>
            {childEstimateTotal && (
              <div className="font-medium text-gray-700">
                Total Est: {formatCurrency(childEstimateTotal.low)}-{formatCurrency(childEstimateTotal.high)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Check for arithmetic errors (labor + material + other != total)
  const otherCosts = (item as any).otherCosts || 0;
  const computedSum = (item.laborCost || 0) + (item.materialCost || 0) + otherCosts;
  const hasArithmeticError = item.laborCost !== null &&
    item.laborCost !== undefined &&
    item.materialCost !== null &&
    item.materialCost !== undefined &&
    item.totalCost !== null &&
    item.totalCost !== undefined &&
    Math.abs(computedSum - item.totalCost) > 0.01;

  const arithmeticErrorDetails = hasArithmeticError &&
    item.totalCost !== null && item.totalCost !== undefined
    ? {
        sum: computedSum,
        difference: Math.abs(computedSum - item.totalCost),
        percentage: (Math.abs(computedSum - item.totalCost) / item.totalCost * 100).toFixed(1)
      }
    : null;

  // Parent item - full card
  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-hidden hover:shadow-sm transition-shadow">
      <div className="px-3 py-2">
        {/* Prominent Error Badges at Top */}
        {(hasArithmeticError || hasMismatch) && (
          <div className="mb-2 space-y-1">
            {hasArithmeticError && arithmeticErrorDetails && (
              <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-red-600" />
                <span>
                  <span className="font-semibold">ARITHMETIC ERROR:</span> laborCost ({formatCurrency(item.laborCost!)}) + materialCost ({formatCurrency(item.materialCost!)}) = {formatCurrency(arithmeticErrorDetails.sum)}, but totalCost = {formatCurrency(item.totalCost!)} (difference: {formatCurrency(arithmeticErrorDetails.difference)}, {arithmeticErrorDetails.percentage}%)
                </span>
              </div>
            )}
            {hasMismatch && childrenSum !== null && item.totalCost !== null && item.totalCost !== undefined && (
              <div className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-300 rounded text-xs text-yellow-900">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-yellow-700" />
                <span>
                  <span className="font-semibold">PARENT CHILD SUM MISMATCH:</span> Children with contractor costs sum to {formatCurrency(childrenSum)} but parent total is {formatCurrency(item.totalCost)}. {formatCurrency(Math.abs(childrenSum - item.totalCost))} unaccounted for ({((Math.abs(childrenSum - item.totalCost) / item.totalCost) * 100).toFixed(1)}%).
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* Expand button */}
          {hasChildren ? (
            <button
              onClick={onToggleExpand}
              className="flex-shrink-0 hover:bg-gray-100 rounded p-0.5 -ml-0.5"
            >
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
              )}
            </button>
          ) : (
            <div className="w-4 flex-shrink-0"></div>
          )}

          {/* Number & Description */}
          <div className="flex-1 min-w-0 flex items-baseline gap-2">
            <span className="text-sm font-semibold text-gray-700 flex-shrink-0">{itemNumber}.</span>
            <h3 className="text-sm text-gray-900">{item.description}</h3>
            {/* Structural badge for parent items */}
            {item.costAnalysis?.isStructural && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded border border-yellow-300">
                ⚠️ Structural
              </span>
            )}
          </div>

          {/* Price section */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {cannotVerify ? (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-500 text-xs font-medium rounded border border-gray-200 whitespace-nowrap"
                title="Fxer couldn't verify this price — the bid lacks the measurements, plans, or counts needed. Not included in Fxer's estimate total."
              >
                <AlertCircle className="h-3 w-3" />
                Unable to verify
              </span>
            ) : fxerEstimateMid !== null ? (
              <div className="text-xs text-gray-400">
                Est: {formatCurrency(fxerEstimateMid)}
              </div>
            ) : null}
            {contractorTotal !== null ? (
              <div className="text-base font-bold text-gray-900 min-w-[90px] text-right">
                {formatCurrency(contractorTotal)}
              </div>
            ) : (
              <div className="text-sm font-medium text-gray-400 min-w-[90px] text-right">No quote</div>
            )}
            {showVariance && (
              <VarianceBadge
                contractorCost={contractorTotal!}
                marketLow={fxerEstimateLow!}
                marketHigh={fxerEstimateHigh!}
                assessment={item.costAnalysis?.assessment}
                size="sm"
                showIcon={true}
              />
            )}
          </div>
        </div>

        {/* Analysis text - compact inline */}
        {item.costAnalysis?.comparisonReason && item.costAnalysis.assessment?.toLowerCase() !== 'reasonable' && (
          <div className="mt-1.5 pl-6 text-xs text-blue-700">
            <span className="font-semibold capitalize">{item.costAnalysis.assessment}:</span> {item.costAnalysis.comparisonReason}
          </div>
        )}

        {/* Critical issues only (excluding mismatch since it's shown prominently above) */}
        {(displayIssues.length > 0 || item.costAnalysis?.flag) && (
          <div className="mt-1.5 pl-6 space-y-0.5">
            {displayIssues.slice(0, 2).map((issue, idx) => (
              <div key={idx} className="flex items-start gap-1 text-xs text-red-700">
                <XCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                <span>{issue.description}</span>
              </div>
            ))}
            {item.costAnalysis?.flag && (
              <div className="flex items-start gap-1 text-xs text-amber-700">
                <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                <span>{item.costAnalysis.flag}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Children as compact rows */}
      {isExpanded && hasChildren && item.children && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="px-4 py-2 flex items-center gap-3 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-100 border-b border-gray-200">
            <span className="w-8">#</span>
            <span className="flex-1">Sub-Item & Estimate Breakdown</span>
            <span className="w-24 text-right">Amount</span>
          </div>
          {item.children.map((child, childIndex) => (
            <LineItemCard
              key={child.id || `child-${childIndex}`}
              item={child}
              index={childIndex}
              level={1}
              parentIndex={index + 1}
              isExpanded={false}
              onToggleExpand={() => {}}
            />
          ))}
        </div>
      )}
    </div>
  );
};
